import { AlertTriangle, Loader2, ShieldCheck } from "lucide-react";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ConcernCard } from "./ConcernCard";
import { useCodeRuleAudit } from "./useCodeRuleAudit";

type CodeRuleAuditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  encounterId: string;
  diagnoses: string[];
  procedures: string[];
  onFinalise: () => void;
  finaliseDisabled: boolean;
};

export function CodeRuleAuditDialog({
  open,
  onOpenChange,
  encounterId,
  diagnoses,
  procedures,
  onFinalise,
  finaliseDisabled,
}: CodeRuleAuditDialogProps) {
  const { runAudit, cancel, data, isPending, isError } = useCodeRuleAudit();

  const showLoading = isPending || (!data && !isError);

  // biome-ignore lint/correctness/useExhaustiveDependencies: run only on the open transition, snapshotting the codes passed at that moment
  useEffect(() => {
    if (open) runAudit({ encounterId, diagnoses, procedures, fresh: true });
  }, [open]);

  const canFinaliseFromHere = !!data && data.concerns.length === 0;

  const handleFinalise = () => {
    handleOpenChange(false);
    onFinalise();
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) cancel();
    onOpenChange(next);
  };

  const body = showLoading ? (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Checking your codes against the trust's coding rules…</p>
      <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
        Cancel
      </Button>
    </div>
  ) : isError ? (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <AlertTriangle className="h-6 w-6 text-destructive" />
      <p className="text-sm">Couldn't run the rule check.</p>
      <Button size="sm" onClick={() => runAudit({ encounterId, diagnoses, procedures, fresh: true })}>
        Retry
      </Button>
    </div>
  ) : data && data.concerns.length === 0 ? (
    <div className="flex flex-col items-center gap-3 py-8 text-center">
      <ShieldCheck className="h-6 w-6 text-emerald-600" />
      <p className="text-sm font-medium">No conflicts with the trust's coding rules</p>
    </div>
  ) : data ? (
    <ScrollArea className="max-h-[60vh] pr-3">
      <ul className="space-y-3">
        {data.concerns.map((concern, index) => (
          <ConcernCard key={`${concern.relatedCodes.join("-")}-${index}`} concern={concern} />
        ))}
      </ul>
    </ScrollArea>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Rule check</DialogTitle>
          {data ? <DialogDescription>{data.summary}</DialogDescription> : null}
        </DialogHeader>

        {body}

        {!showLoading ? (
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
              Close
            </Button>
            {canFinaliseFromHere ? (
              <Button size="sm" onClick={handleFinalise} disabled={finaliseDisabled}>
                Finalise
              </Button>
            ) : null}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
